import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = anon
  const [error, setError] = useState("");

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("qdurator_token");
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("qdurator_user", JSON.stringify(data));
    } catch (e) {
      localStorage.removeItem("qdurator_token");
      localStorage.removeItem("qdurator_user");
      setUser(null);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("qdurator_token", data.access_token);
      localStorage.setItem("qdurator_user", JSON.stringify(data.user));
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const register = async (email, password, name, invite_token = null) => {
    setError("");
    try {
      const url = invite_token
        ? `/auth/register?invite_token=${encodeURIComponent(invite_token)}`
        : `/auth/register`;
      const { data } = await api.post(url, { email, password, name });
      localStorage.setItem("qdurator_token", data.access_token);
      localStorage.setItem("qdurator_user", JSON.stringify(data.user));
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem("qdurator_token");
    localStorage.removeItem("qdurator_user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, error, setError, login, register, logout, refreshMe: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
